import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { PercentChangeDisplay } from './percent-change-display';
import { SCREEN_PADDING } from '@/styles';
import { Text, View } from 'react-native';
import { TotalSalesTableData } from './types';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface Props {
  data: TotalSalesTableData;
}

export const TotalSalesTable: React.FC<Props> = ({ data }) => {
  const {
    avgOrderSize,
    grossProfitInMicros,
    grossProfitPercentOverPeriod,
    totalOrders,
    totalSalesInMicros,
    totalSalesPercentOverPeriod,
  } = data;

  const { currencyCode } = useVariantPackInfoContext();
  const [styles] = useTotalSalesTableStyles();

  return (
    <View style={styles.base}>
      <View style={styles.row}>
        <View style={styles.col}>
          <Text style={styles.title}>
            {i18n.t('dsr_analytics.total_sales.table.total_orders.title')}
          </Text>
          <Text style={styles.content}>{totalOrders}</Text>
        </View>
        <View style={styles.col}>
          <Text style={[styles.title]}>
            {i18n.t('dsr_analytics.total_sales.table.total_sales.title')}
          </Text>
          <Text style={styles.content}>
            {formatCurrency(totalSalesInMicros, currencyCode)}
          </Text>
          <PercentChangeDisplay percent={totalSalesPercentOverPeriod} />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.col}>
          <Text style={styles.title}>
            {i18n.t('dsr_analytics.total_sales.table.average_order_size.title')}
          </Text>
          <Text style={styles.content}>
            {i18n.t(
              'dsr_analytics.total_sales.table.average_order_size.value',
              { avgOrderSize: avgOrderSize },
            )}
          </Text>
        </View>
        {/* grossProfitInMicros is only populated by demo/mock data (Bronco);
            the live query path always sets it to null */}
        {grossProfitInMicros ? (
          <View style={styles.col}>
            <Text style={styles.title}>
              {i18n.t('dsr_analytics.total_sales.table.gross_profit.title')}
            </Text>
            <Text style={styles.content}>
              {formatCurrency(grossProfitInMicros, currencyCode)}
            </Text>
            <PercentChangeDisplay percent={grossProfitPercentOverPeriod} />
          </View>
        ) : null}
      </View>
    </View>
  );
};

const useTotalSalesTableStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'column',
    paddingHorizontal: SCREEN_PADDING,
  },
  col: {
    flex: 1,
    flexDirection: 'column',
    gap: 4,
  },
  content: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: 21,
  },
  row: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 8,
  },
  title: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: 11,
    textTransform: 'uppercase',
  },
}));
